"use client";

import { useState } from "react";
import {
  ErrorNotice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { ECON_CHART_STRINGS } from "@/components/tools/econ-micro/chart-strings";
import { MarketGraph } from "@/components/tools/econ-micro/market-graph";
import {
  type CurveForm,
  MarketView,
} from "@/components/tools/econ-micro/market-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";
import { type SolveError, solveMarket } from "@/lib/tool-kit/econ-micro/market";

/** English strings. A translated content file overrides these through `ui`. */
export const SUPPLY_AND_DEMAND_CALCULATOR_STRINGS = {
  ...ECON_CHART_STRINGS,
  formLabel: "Enter the curves as",
  formQuantity: "Quantity form: Qd = a − bP and Qs = c + dP",
  formPrice: "Price form: P = a − bQ and P = c + dQ",
  curvesHeading: "Demand and supply",
  curvesHint:
    "Both slopes (b and d) must be above 0. The intercepts a and c can be any number, and c is often negative.",
  quantityDemandA: "Demand intercept a (Qd = a − bP)",
  quantityDemandB: "Demand slope b",
  quantitySupplyC: "Supply intercept c (Qs = c + dP)",
  quantitySupplyD: "Supply slope d",
  priceDemandA: "Demand price intercept a (P = a − bQ)",
  priceDemandB: "Demand slope b",
  priceSupplyC: "Supply price intercept c (P = c + dQ)",
  priceSupplyD: "Supply slope d",
  interventionLabel: "Government intervention",
  interventionNone: "None",
  interventionTax: "Per-unit tax",
  interventionSubsidy: "Per-unit subsidy",
  amountTax: "Tax per unit (t)",
  amountSubsidy: "Subsidy per unit (s)",
  interventionHint:
    "It makes no difference to the result whether the law taxes buyers or sellers: the gap between the price buyers pay and the price sellers keep is the same.",
  results: "Results",
  invalidInput:
    "Enter all four curve numbers, and an amount of 0 or more for any tax or subsidy.",
  badSlope: "The slopes b and d must both be greater than 0.",
  noTrade:
    "No trade happens: at every quantity, buyers are willing to pay less than sellers need. The demand line has to start above the supply line.",
  negativePrice:
    "The lines cross at a negative price, so there is no sensible equilibrium. Check the signs of a and c.",
  prohibitive:
    "That tax is at least as large as the gap between the highest price buyers will pay and the lowest price sellers will accept, so the market would shut down. Try a smaller tax.",
  negativeBuyerPrice:
    "That subsidy is so large that buyers would be paid to take the good. Try a smaller subsidy.",
  statPrice: "Equilibrium price (P*)",
  statQuantity: "Equilibrium quantity (Q*)",
  statConsumerSurplus: "Consumer surplus",
  statProducerSurplus: "Producer surplus",
  statTotalSurplus: "Total surplus",
  statElasticities: "Elasticity at equilibrium (|Ed| · Es)",
  workingHeading: "Working",
  workEquateQuantity: "Set Qd = Qs: {demand} = {supply}",
  workEquatePrice: "Set the two prices equal: {demand} = {supply}",
  workSolvePrice:
    "P* = (a − c) ÷ (b + d) = ({a} − {c}) ÷ ({b} + {d}) = {result}",
  workSolveQuantity:
    "Q* = (a − c) ÷ (b + d) = ({a} − {c}) ÷ ({b} + {d}) = {result}",
  workQuantityFromPrice: "Q* = a − bP* = {a} − {b} × {solved} = {result}",
  workPriceFromQuantity: "P* = a − bQ* = {a} − {b} × {solved} = {result}",
  workIntercepts: "Price intercepts: demand α = {alpha}, supply γ = {gamma}",
  workConsumerSurplus:
    "Consumer surplus = ½ × (α − P*) × Q* = ½ × ({alpha} − {p}) × {q} = {cs}",
  workProducerSurplus:
    "Producer surplus = ½ × (P* − γ) × Q* = ½ × ({p} − {gamma}) × {q} = {ps}",
  negativeInterceptNote:
    "The supply line meets the price axis below zero, so the producer surplus triangle is measured down to that intercept, as most textbooks do. The graph shows only the part above zero.",
  taxHeading: "Effect of the tax",
  subsidyHeading: "Effect of the subsidy",
  wedgeTax:
    "A tax of {t} per unit drives a wedge between the price buyers pay (Pb) and the price sellers keep (Ps): Pb − Ps = {t}. The new quantity is where the demand line sits {t} above the supply line.",
  wedgeSubsidy:
    "A subsidy of {t} per unit drives a wedge the other way: sellers receive {t} more than buyers pay, Ps − Pb = {t}. The new quantity is where the supply line sits {t} above the demand line.",
  workQuantityQuantityForm:
    "Q = Q* − t × bd ÷ (b + d) = {qstar} − {t} × ({b} × {d}) ÷ ({b} + {d}) = {result}",
  workQuantityPriceForm:
    "Q = Q* − t ÷ (b + d) = {qstar} − {t} ÷ ({b} + {d}) = {result}",
  workBuyerPrice:
    "Buyers pay Pb, the price on the demand line at Q = {q}: {pb}",
  workSellerPrice: "Sellers keep Ps = Pb − t = {pb} − {t} = {ps}",
  workRevenueTax: "Tax revenue = t × Q = {t} × {q} = {result}",
  workRevenueSubsidy: "Cost to government = s × Q = {t} × {q} = {result}",
  workLoss: "Deadweight loss = ½ × t × |Q* − Q| = ½ × {t} × {dq} = {result}",
  incidenceTax:
    "Buyers’ price is {buyers} {buyerSide} ({buyerShare} of the tax); sellers’ price is {sellers} {sellerSide} ({sellerShare}).",
  incidenceSubsidy:
    "Buyers’ price is {buyers} {buyerSide} ({buyerShare} of the subsidy); sellers’ price is {sellers} {sellerSide} ({sellerShare}).",
  higher: "higher",
  lower: "lower",
  incidenceRuleTax:
    "The side with the less elastic curve bears more of the tax. Buyers’ share = Es ÷ (Es + |Ed|), with Es = {es} and |Ed| = {ed} at the original equilibrium.",
  incidenceRuleSubsidy:
    "The side with the less elastic curve gets more of the subsidy. Buyers’ share = Es ÷ (Es + |Ed|), with Es = {es} and |Ed| = {ed} at the original equilibrium.",
  tableMeasure: "Measure",
  tableBefore: "Before",
  tableAfter: "After",
  tableChange: "Change",
  tableBuyerPrice: "Price buyers pay",
  tableSellerPrice: "Price sellers receive",
  tableQuantity: "Quantity",
  tableRevenue: "Tax revenue",
  tableSubsidyBill: "Subsidy paid (counted as a cost)",
  tableTotalWelfare: "Total welfare (surplus plus government budget)",
  tableLoss: "Deadweight loss",
  graphTitle: "Supply and demand",
  priceAxis: "Price (P)",
  quantityAxis: "Quantity (Q)",
  curveDemand: "D",
  curveSupply: "S",
  curveSupplyTax: "S + tax",
  curveSupplySubsidy: "S − subsidy",
  pointEquilibrium: "Equilibrium",
  pointEquilibriumShort: "E",
  pointBuyers: "Buyers pay",
  pointSellers: "Sellers keep",
  legendConsumer: "Consumer surplus",
  legendProducer: "Producer surplus",
  legendRevenue: "Tax revenue",
  legendSubsidyCost: "Subsidy cost",
  legendLoss: "Deadweight loss",
  tablePoint: "Point",
  graphDescription:
    "Supply and demand graph with price on the vertical axis and quantity on the horizontal axis. Demand slopes down from a price of {demandStart}; supply slopes up from a price of {supplyStart}. They cross at quantity {q} and price {p}.",
  graphDescriptionAfter:
    "With the intervention, quantity is {qt}, buyers pay {pb} and sellers receive {ps}.",
} as const;

export type SupplyAndDemandStrings =
  typeof SUPPLY_AND_DEMAND_CALCULATOR_STRINGS;
type Intervention = "none" | "tax" | "subsidy";

interface Inputs {
  a: string;
  b: string;
  c: string;
  d: string;
  amount: string;
}

const START: Record<CurveForm, Inputs> = {
  quantity: { a: "120", b: "2", c: "-30", d: "3", amount: "5" },
  price: { a: "100", b: "2", c: "10", d: "1", amount: "12" },
};

const NO_NUMBERS = { a: 0, b: 0, c: 0, d: 0 };

/** Read the fields and solve the market; `solved` is null while a field is unreadable. */
function evaluate(form: CurveForm, raw: Inputs, kind: Intervention) {
  const [a, b, c, d] = [raw.a, raw.b, raw.c, raw.d].map(parseNumberInput);
  const amount = parseNumberInput(raw.amount);
  const amountOk = kind === "none" || (amount !== null && amount >= 0);
  if (a === null || b === null || c === null || d === null || !amountOk) {
    return { solved: null, numbers: NO_NUMBERS };
  }
  const sign = kind === "subsidy" ? -1 : 1;
  const perUnit = kind === "none" ? 0 : sign * (amount ?? 0);
  const numbers = { a, b, c, d };
  return { solved: solveMarket(form, numbers, perUnit), numbers };
}

export default function SupplyAndDemandCalculatorWidget() {
  const strings = useToolUi(SUPPLY_AND_DEMAND_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<CurveForm>("quantity");
  const [inputs, setInputs] = useState(START);
  const [kind, setKind] = useState<Intervention>("tax");
  const raw = inputs[form];
  const set = (key: keyof Inputs) => (value: string) =>
    setInputs((all) => ({ ...all, [form]: { ...all[form], [key]: value } }));

  const labels =
    form === "quantity"
      ? [
          strings.quantityDemandA,
          strings.quantityDemandB,
          strings.quantitySupplyC,
          strings.quantitySupplyD,
        ]
      : [
          strings.priceDemandA,
          strings.priceDemandB,
          strings.priceSupplyC,
          strings.priceSupplyD,
        ];

  const { solved, numbers } = evaluate(form, raw, kind);
  const errors: Record<SolveError, string> = {
    "bad-slope": strings.badSlope,
    "no-trade": strings.noTrade,
    "negative-price": strings.negativePrice,
    prohibitive: strings.prohibitive,
    "negative-buyer-price": strings.negativeBuyerPrice,
  };
  let problem: string | null = null;
  if (!solved) {
    problem = strings.invalidInput;
  } else if (solved.error) {
    problem = errors[solved.error];
  }

  return (
    <div className="space-y-6">
      <SelectField
        id="sd-form"
        label={strings.formLabel}
        onChange={setForm}
        options={[
          { value: "quantity", label: strings.formQuantity },
          { value: "price", label: strings.formPrice },
        ]}
        value={form}
      />
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.curvesHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.curvesHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="sd-a"
            label={labels[0]}
            numeric
            onChange={set("a")}
            value={raw.a}
          />
          <TextField
            id="sd-c"
            label={labels[2]}
            numeric
            onChange={set("c")}
            value={raw.c}
          />
          <TextField
            id="sd-b"
            label={labels[1]}
            numeric
            onChange={set("b")}
            value={raw.b}
          />
          <TextField
            id="sd-d"
            label={labels[3]}
            numeric
            onChange={set("d")}
            value={raw.d}
          />
        </div>
        <PillGroup
          label={strings.interventionLabel}
          name="sd-intervention"
          onChange={setKind}
          options={[
            { value: "none", label: strings.interventionNone },
            { value: "tax", label: strings.interventionTax },
            { value: "subsidy", label: strings.interventionSubsidy },
          ]}
          value={kind}
        />
        {kind === "none" ? null : (
          <div className="max-w-xs">
            <TextField
              hint={strings.interventionHint}
              id="sd-amount"
              label={kind === "tax" ? strings.amountTax : strings.amountSubsidy}
              numeric
              onChange={set("amount")}
              value={raw.amount}
            />
          </div>
        )}
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {solved && !solved.error ? (
          <>
            <MarketView
              {...solved}
              fmt={fmt}
              form={form}
              raw={numbers}
              strings={strings}
            />
            <MarketGraph {...solved} fmt={fmt} strings={strings} />
          </>
        ) : null}
      </section>
    </div>
  );
}
